import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import styles from "./ChoiceMenu.module.css";

// ChoiceMenu (C06/C08 del catálogo): mismo HTML que el componente de Bheli.Web.Feedback. El menú es un popover nativo
// anclado con CSS (position-anchor), igual que en el portal actual: misma posición, mismo foco y mismas teclas.
// R8 (C08 1.14.8): con `onSearch`, los datos que crecen buscan en el servidor (el buscador siempre a la vista y sin
// filtrar aquí; quien lo usa espera 50 ms tras la última tecla, useServerOptions). Con `values`, varias opciones con ✓:
// el menú queda abierto mientras se elige y el botón muestra `summary`.
// RC1 (C08 1.14.9, C27 1.14.9): es el único selector con buscador del Admin (E48): el menú mide al menos el ancho de su
// campo y empieza en su borde izquierdo; en el celular es la hoja inferior con su superficie.

export type ChoiceOption = { value: string; label: string; disabled?: boolean };

/** Con más de 6 opciones (o mientras quede texto escrito) aparece el buscador, si quien lo usa pasa su etiqueta. */
export const searchThreshold = 6;

type Props = {
  id?: string;
  label: string;
  value: string;
  options: ChoiceOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  searchLabel?: string;
  clearSearchLabel?: string;
  searchIcon?: ReactNode;
  clearIcon?: ReactNode;
  describedBy?: string;
  invalid?: boolean;
  chevron?: ReactNode;
  check?: ReactNode;
  match?: (option: ChoiceOption, search: string) => boolean;
  placeholder?: string;
  /** Búsqueda en el servidor: recibe cada cambio del buscador; las opciones ya vienen filtradas. */
  onSearch?: (text: string) => void;
  /** Texto del valor elegido cuando no está entre las opciones de la búsqueda actual. */
  currentLabel?: string;
  /** Línea al final del menú (cargando, sin resultados, «N de M»). */
  note?: ReactNode;
  /** Varias opciones: las elegidas; `onChange` recibe la opción tocada (agregar o quitar). */
  values?: string[];
  /** Varias opciones: lo que dice el botón («Ninguno», «Atender chats y 2 más»). */
  summary?: string;
};

const fold = (text: string) => text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export function ChoiceMenu(props: Props) {
  const generated = useId().replace(/:/g, "");
  const id = props.id ?? `choice-${generated}`;
  const menuId = `${id}-menu`;
  const [search, setSearch] = useState("");
  const menu = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const s = styles.scope;
  const { options, match, onSearch, values } = props;
  const server = !!onSearch;
  const multi = !!values;
  const showSearch = !!props.searchLabel && (server || props.options.length > searchThreshold || search.length > 0);
  const current = multi
    ? (props.summary ?? props.placeholder ?? "—")
    : (props.options.find((option) => option.value === props.value)?.label ??
      props.currentLabel ??
      props.placeholder ??
      "—");
  const empty = multi ? values.length === 0 : !props.value;
  const visible = useMemo(
    () =>
      server
        ? options
        : options.filter(
            (option) =>
              search.length === 0 || (match ? match(option, search) : fold(option.label).includes(fold(search))),
          ),
    [options, match, search, server],
  );
  const searchFor = (text: string) => {
    setSearch(text);
    onSearch?.(text);
  };
  // Esc en el buscador (escuchado una vez) limpia también la búsqueda del servidor.
  const setSearchRef = useRef<(text: string) => void>(() => undefined);
  useEffect(() => {
    setSearchRef.current = searchFor;
  });

  useEffect(() => {
    const element = menu.current;
    if (!element) return;
    // Al abrir con puntero fino en escritorio, el foco va al buscador o a la opción elegida; al cerrar, vuelve al botón.
    const onToggle = (event: Event) => {
      if ((event as ToggleEvent).newState === "open") {
        if (matchMedia("(pointer:fine)").matches && matchMedia("(min-width:768px)").matches)
          (
            element.querySelector<HTMLElement>("input") ??
            element.querySelector<HTMLElement>('[aria-checked="true"]') ??
            element.querySelector<HTMLElement>("button")
          )?.focus();
      } else trigger.current?.focus();
    };
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (event.key === "Escape" && target.matches("input[type=search]") && (target as HTMLInputElement).value) {
        event.preventDefault();
        event.stopPropagation();
        setSearchRef.current("");
        return;
      }
      if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
      const items = [...element.querySelectorAll<HTMLButtonElement>("button:not(:disabled)")];
      const index = items.indexOf(document.activeElement as HTMLButtonElement);
      const next =
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? items.length - 1
            : (index + (event.key === "ArrowUp" ? -1 : 1) + items.length) % items.length;
      items[next]?.focus();
      event.preventDefault();
    };
    // RC1 · K2 (C08 1.14.9, E110): antes de abrir, el menú toma el ancho de su campo (mínimo; crece hasta 480 px).
    const onBeforeToggle = (event: Event) => {
      if ((event as ToggleEvent).newState !== "open" || !trigger.current) return;
      element.style.setProperty("--bh-field-width", `${Math.round(trigger.current.getBoundingClientRect().width)}px`);
    };
    element.addEventListener("beforetoggle", onBeforeToggle);
    element.addEventListener("toggle", onToggle);
    element.addEventListener("keydown", onKey);
    return () => {
      element.removeEventListener("beforetoggle", onBeforeToggle);
      element.removeEventListener("toggle", onToggle);
      element.removeEventListener("keydown", onKey);
    };
  }, []);

  const choose = (value: string) => {
    // Varias opciones: el menú queda abierto y conserva la búsqueda (C08, «queda abierto mientras eliges»).
    if (multi) return props.onChange(value);
    menu.current?.hidePopover();
    if (search) searchFor("");
    props.onChange(value);
  };

  return (
    <div className={`bh-choice ${s}`}>
      <button
        ref={trigger}
        id={id}
        type="button"
        className={`bh-choice-trigger ${s}`}
        disabled={props.disabled}
        popoverTarget={menuId}
        aria-label={props.label}
        aria-haspopup="menu"
        aria-describedby={props.describedBy}
        aria-invalid={props.invalid ? "true" : "false"}
      >
        <span className={s} data-empty={empty ? "true" : undefined}>
          {current}
        </span>
        {props.chevron}
      </button>
      <div ref={menu} id={menuId} popover="auto" className={`bh-choice-menu ${s}`} role="menu" aria-label={props.label}>
        {showSearch ? (
          <div className={`bh-choice-search ${s}`}>
            {props.searchIcon}
            <input
              className={s}
              type="search"
              enterKeyHint="search"
              aria-label={props.searchLabel}
              placeholder={props.searchLabel}
              value={search}
              onChange={(event) => searchFor(event.target.value)}
            />
            {search.length > 0 ? (
              <button
                type="button"
                className={`bh-choice-clear ${s}`}
                aria-label={props.clearSearchLabel}
                onClick={() => {
                  searchFor("");
                  menu.current?.querySelector<HTMLInputElement>("input[type=search]")?.focus();
                }}
              >
                {props.clearIcon}
              </button>
            ) : null}
          </div>
        ) : null}
        {visible.map((option) => {
          const on = multi ? values.includes(option.value) : props.value === option.value;
          return (
            <button
              key={option.value}
              className={s}
              type="button"
              role={multi ? "menuitemcheckbox" : "menuitemradio"}
              aria-checked={on ? "true" : "false"}
              disabled={option.disabled}
              onClick={() => choose(option.value)}
            >
              <span className={s}>{option.label}</span>
              {on ? props.check : null}
            </button>
          );
        })}
        {props.note ? (
          <p className={`bh-choice-note ${s}`} role="status">
            {props.note}
          </p>
        ) : null}
      </div>
    </div>
  );
}
